import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { gaTrackEvent, gaTrackPageView } from "../analytics/ga4";
import { getAuthContext } from "../config/runtime";
import { BOTTOM_NAV_HEIGHT_PX, pageGutterPx } from "../core/breakpoints";
import { C, LOCALE_STORAGE_KEY } from "../core/constants";
import { IOS_PICKER_KEEP_ALIVE_PAGES, isIosWebkit, nextPickerSlotState, recoverIosScrollLayer } from "../core/iosWebkit";
import { NativePickerSuspendProvider } from "../core/NativePickerInput";
import { APP_FONT_SIZE } from "../core/theme";
import { fetchBillingCatalog } from "../features/billing/api";
import { PRODUCT_CODE_BY_PACK_KEY, SUBSCRIPTION_PRODUCT_CODE } from "../features/billing/config";
import MembershipSection from "../features/credits/components/MembershipSection";
import { fetchPlanetaryPositions } from "../features/dashboard/api";
import { formatTopbarTransitHighlight, pickSalientTransitPlanet } from "../features/dashboard/topbarTransitStatus";
import { useIsMobile } from "../hooks/useMediaQuery";
import { useStarfieldScrollPause } from "../hooks/useStarfieldScrollPause";
import { resolveCurrentPage, resolveRedirectForPage } from "./routing";
import { blurActiveElement, buildPageUrl, hardNavigateToUrl, navigateToPageUrl, pushAppUrl } from "./spaNavigation";

export default function AppShell({
  locale,
  setLocale,
  acc,
  authContext,
  initialPage,
  t,
  components,
  helpers,
  timezone,
  timezoneLabel,
  formatInTimezone,
}) {
  const [selectedPaymentProduct, setSelectedPaymentProduct] = useState(null);
  const [billingCatalog, setBillingCatalog] = useState([]);
  const [salientTransit, setSalientTransit] = useState(null);
  const [locationPath, setLocationPath] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : "/"
  );
  const [locationSearch, setLocationSearch] = useState(() =>
    typeof window !== "undefined" ? window.location.search : ""
  );
  const [pageResetKeys, setPageResetKeys] = useState({});
  const [keptPickerPages, setKeptPickerPages] = useState(() => new Set());
  const [revealedPickerPage, setRevealedPickerPage] = useState(() => {
    const page = resolveCurrentPage(initialPage, typeof window !== "undefined" ? window.location.pathname : "/");
    return IOS_PICKER_KEEP_ALIVE_PAGES.includes(page) ? page : null;
  });
  const [demotedPickerPage, setDemotedPickerPage] = useState(null);
  const mainScrollRef = useRef(null);

  const accountUser = authContext.user || {};
  const activeSubscription = Boolean(accountUser.activeSubscription ?? accountUser.active_subscription);
  const [creditsBalance, setCreditsBalance] = useState(() => {
    const raw = Number(accountUser.creditsBalance ?? accountUser.credits_balance ?? 0);
    return Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0;
  });
  const updateCreditsBalance = useCallback((nextBalance) => {
    const normalized = Number(nextBalance);
    const safeValue = Number.isFinite(normalized) ? Math.max(0, Math.floor(normalized)) : 0;
    setCreditsBalance(safeValue);
    if (authContext.user) {
      authContext.user.creditsBalance = safeValue;
      authContext.user.credits_balance = safeValue;
    }
  }, [authContext]);
  useEffect(() => {
    const raw = Number(accountUser.creditsBalance ?? accountUser.credits_balance ?? 0);
    const safeValue = Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0;
    setCreditsBalance(safeValue);
  }, [accountUser.creditsBalance, accountUser.credits_balance]);

  const displayName = accountUser.fullName || accountUser.email?.split("@")[0] || t("dashboard_guest_name");
  const today = useMemo(() => new Date(), []);
  const dashboardMeta = useMemo(() => {
    const birthDate = accountUser.birthDate || "";
    const moonSign = helpers.getMoonSignLabel(today, t);
    const transitHighlight = formatTopbarTransitHighlight(salientTransit, t);
    const cosmicStatus = t("topbar_cosmic_status")
      .replace("%{moonSign}", moonSign)
      .replace("%{transitHighlight}", transitHighlight);

    return {
      creditsBalance,
      activeSubscription,
      displayName,
      greeting: helpers.getDashboardGreeting(today, t),
      longDate: helpers.formatDashboardDate(today, locale),
      shortDate: helpers.formatDashboardShortDate(today, locale),
      moonSign,
      cosmicStatus,
      // Subtitle should reflect user's personal (natal) sun sign.
      sunSign: helpers.getSunSignLabel(birthDate || today.toISOString().slice(0, 10), t),
      timezoneLabel,
    };
  }, [accountUser.birthDate, activeSubscription, creditsBalance, displayName, helpers, locale, salientTransit, t, timezoneLabel, today]);

  const currentPage = resolveCurrentPage(initialPage, locationPath);

  useEffect(() => {
    const syncFromWindow = () => {
      blurActiveElement();
      setLocationPath(window.location.pathname);
      setLocationSearch(window.location.search);
    };
    window.addEventListener("popstate", syncFromWindow);
    return () => window.removeEventListener("popstate", syncFromWindow);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(locationSearch || "");
    const oauthLoginMethod = params.get("ga_login");
    if (oauthLoginMethod) {
      gaTrackEvent("login", { method: oauthLoginMethod });
      params.delete("ga_login");
      const q = params.toString();
      window.history.replaceState(
        {},
        "",
        `${locationPath}${q ? `?${q}` : ""}${window.location.hash || ""}`
      );
      setLocationSearch(q ? `?${q}` : "");
    }

    gaTrackPageView({
      pagePath: `${locationPath}${locationSearch}`,
      pageTitle: typeof document !== "undefined" ? document.title : "",
      pageLocation: typeof window !== "undefined" ? window.location.href : "",
      screenClass: currentPage,
    });
  }, [currentPage, locale, locationPath, locationSearch]);

  const replayContext = useMemo(() => {
    const params = new URLSearchParams(locationSearch || "");
    const predictionId = params.get("predictionId");
    const replay = params.get("replay");
    if (!predictionId || replay !== "1") return null;
    return { predictionId };
  }, [locationSearch]);

  const isSignedInContact = getAuthContext().signedIn && currentPage === "contact";
  const publicOnlyPages = new Set([
    "about",
    "contact",
    "emailVerificationRequired",
    "landing",
    "login",
    "payment",
    "pricing",
    "privacy",
    "terms",
  ]);
  const inApp = isSignedInContact || !publicOnlyPages.has(currentPage);
  const isMobile = useIsMobile();
  const mobileInApp = inApp && isMobile;
  useStarfieldScrollPause(mainScrollRef);

  const navigateToPage = useCallback((nextPage, options = {}) => {
    const targetUrl = buildPageUrl(nextPage, locale, options?.query || null);
    const currentUrl = `${locationPath}${locationSearch}`;

    if (options?.reset) {
      setPageResetKeys((prev) => ({
        ...prev,
        [nextPage]: (prev[nextPage] || 0) + 1,
      }));
    }

    if (currentUrl === targetUrl) {
      if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
      return;
    }

    if (!navigateToPageUrl(nextPage, locale, options)) return;

    setLocationPath(window.location.pathname);
    setLocationSearch(window.location.search);
    if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
  }, [locale, locationPath, locationSearch]);

  useEffect(() => {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch (_) {
      // ignore storage failures
    }
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    document.documentElement.style.setProperty("--ecliptica-glow-alpha", String(acc.glowAlpha));
  }, [acc.glowAlpha]);

  useEffect(() => {
    let cancelled = false;
    fetchBillingCatalog()
      .then((payload) => {
        if (!cancelled) setBillingCatalog(payload?.products || []);
      })
      .catch(() => {
        if (!cancelled) setBillingCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!authContext.signedIn) {
      setSalientTransit(null);
      return;
    }

    let cancelled = false;
    fetchPlanetaryPositions({ locale })
      .then((payload) => {
        if (cancelled) return;
        const picked = pickSalientTransitPlanet(payload?.planetary_positions);
        if (!picked) {
          setSalientTransit(null);
          return;
        }
        setSalientTransit({ planet: picked.planet, retrograde: picked.retrograde });
      })
      .catch(() => {
        if (!cancelled) setSalientTransit(null);
      });

    return () => {
      cancelled = true;
    };
  }, [authContext.signedIn, locale]);

  // Persisted selection via URL query params:
  // - navigateToPage('payment', { query: { product_code, product_type } })
  // - On reload, rehydrate `selectedPaymentProduct` from billingCatalog.
  useEffect(() => {
    if (currentPage !== "payment") return;

    const params = new URLSearchParams(locationSearch || "");
    const productCode = params.get("product_code");
    const _productType = params.get("product_type");
    if (!productCode) return;
    if (!billingCatalog || billingCatalog.length === 0) return;

    const found = billingCatalog.find((item) => item.code === productCode) || null;
    setSelectedPaymentProduct(found);
  }, [currentPage, billingCatalog, locationSearch]);

  useEffect(() => {
    if (!IOS_PICKER_KEEP_ALIVE_PAGES.includes(currentPage)) return;
    setKeptPickerPages((prev) => (prev.has(currentPage) ? prev : new Set(prev).add(currentPage)));
  }, [currentPage]);

  useLayoutEffect(() => {
    const next = nextPickerSlotState(
      { revealed: revealedPickerPage, demoted: demotedPickerPage },
      currentPage,
      isIosWebkit()
    );
    if (next.revealed === revealedPickerPage && next.demoted === demotedPickerPage) {
      recoverIosScrollLayer(mainScrollRef.current);
      return undefined;
    }
    if (next.waitForPaint) {
      const frame = requestAnimationFrame(() => {
        setRevealedPickerPage(next.revealed);
        setDemotedPickerPage(next.demoted);
      });
      return () => cancelAnimationFrame(frame);
    }
    setRevealedPickerPage(next.revealed);
    setDemotedPickerPage(next.demoted);
    return undefined;
  }, [currentPage, demotedPickerPage, revealedPickerPage]);

  const renderSignedInPage = (page) => {
    const resetPage = (target) => () => navigateToPage(target, { reset: true });

    switch (page) {
      case "dashboard":
        return (
          <components.DashboardHome
            acc={acc}
            setPage={navigateToPage}
            dashboardMeta={dashboardMeta}
            locale={locale}
            formatInTimezone={formatInTimezone}
            timezoneLabel={timezoneLabel}
          />
        );
      case "western":
        return (
          <components.WesternAstrology
            key={pageResetKeys.western || 0}
            acc={acc}
            onCreditsUpdate={updateCreditsBalance}
            replayPredictionId={replayContext?.predictionId}
            onResetPage={resetPage("western")}
          />
        );
      case "chinese":
        return (
          <components.ChineseZodiac
            key={pageResetKeys.chinese || 0}
            acc={acc}
            onCreditsUpdate={updateCreditsBalance}
            replayPredictionId={replayContext?.predictionId}
            onResetPage={resetPage("chinese")}
          />
        );
      case "tarot":
        return (
          <components.TarotReading
            key={pageResetKeys.tarot || 0}
            acc={acc}
            onCreditsUpdate={updateCreditsBalance}
            replayPredictionId={replayContext?.predictionId}
            onResetPage={resetPage("tarot")}
          />
        );
      case "wedding":
        return (
          <components.WeddingCompatibility
            key={pageResetKeys.wedding || 0}
            acc={acc}
            onCreditsUpdate={updateCreditsBalance}
            replayPredictionId={replayContext?.predictionId}
            onResetPage={resetPage("wedding")}
          />
        );
      case "coupleMatch":
        return (
          <components.CoupleMatch
            key={pageResetKeys.coupleMatch || 0}
            acc={acc}
            onCreditsUpdate={updateCreditsBalance}
            replayPredictionId={replayContext?.predictionId}
            onResetPage={resetPage("coupleMatch")}
          />
        );
      case "settings":
        return <components.Settings acc={acc} authContext={authContext} t={t} locale={locale} timezone={timezone} />;
      case "contact":
        return <components.ContactPage acc={acc} />;
      case "payment":
        return (
          <components.PaymentPage
            setPage={navigateToPage}
            acc={acc}
            selectedProduct={selectedPaymentProduct}
            billingCatalog={billingCatalog}
            t={t}
          />
        );
      case "readingHistory":
        return (
          <components.ReadingHistoryPage
            acc={acc}
            setPage={navigateToPage}
            locale={locale}
            formatInTimezone={formatInTimezone}
          />
        );
      default:
        return null;
    }
  };

  const pickerKeepAlivePages = IOS_PICKER_KEEP_ALIVE_PAGES.filter(
    (page) => page === currentPage || keptPickerPages.has(page)
  );

  useEffect(() => {
    const legalFooter = document.getElementById("home-static-legal");
    if (!legalFooter) return undefined;

    if (currentPage !== "landing") {
      legalFooter.classList.remove("is-visible");
      return undefined;
    }

    const updateVisibility = () => {
      const doc = document.documentElement;
      const thresholdPx = 72;
      const atBottom = window.innerHeight + window.scrollY >= doc.scrollHeight - thresholdPx;
      legalFooter.classList.toggle("is-visible", atBottom);
    };

    updateVisibility();
    window.addEventListener("scroll", updateVisibility, { passive: true });
    window.addEventListener("resize", updateVisibility);
    return () => {
      window.removeEventListener("scroll", updateVisibility);
      window.removeEventListener("resize", updateVisibility);
    };
  }, [currentPage]);

  const handleLocaleNavigate = useCallback((targetPath, nextLocale) => {
    pushAppUrl(targetPath, { state: { locale: nextLocale } });
    setLocale(nextLocale);
    setLocationPath(targetPath);
    setLocationSearch(window.location.search);
    if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
  }, [setLocale]);

  const publicShellProps = {
    acc,
    t,
    navigateToPage,
    locale,
    setLocale,
    onLocaleNavigate: handleLocaleNavigate,
    LanguageSwitcher: components.LanguageSwitcher,
  };
  const minimalLocaleStyle = {
    position: "fixed",
    top: isMobile ? 16 : 84,
    right: 16,
    zIndex: 41,
  };

  const redirectPath = resolveRedirectForPage({ currentPage });
  if (redirectPath) {
    hardNavigateToUrl(redirectPath);
    return null;
  }

  return (
    <div style={{ minHeight: "100vh", position: "relative", zIndex: 1 }}>
      <components.Starfield inApp={inApp} isMobile={isMobile} />
      <div style={{ position: "fixed", inset: 0, background: `radial-gradient(ellipse 60% 50% at 20% 80%, ${acc.tertiary}08 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 80% 20%, ${acc.primary}0a 0%, transparent 60%)`, pointerEvents: "none", zIndex: 0 }} />

      {inApp ? (
        <div
          className={mobileInApp ? "ecliptica-app-shell ecliptica-app-shell--mobile" : "ecliptica-app-shell"}
          style={{ display: "flex", minHeight: "100vh", position: "relative", zIndex: 1 }}
        >
          {!mobileInApp && (
            <components.Sidebar
              page={currentPage}
              setPage={navigateToPage}
              acc={acc}
              creditsBalance={creditsBalance}
              activeSubscription={activeSubscription}
              showCreditsChip={currentPage !== "creditsPlans"}
              analyticsScreen={currentPage}
            />
          )}
          <div
            ref={mainScrollRef}
            className="ecliptica-main-scroll"
            style={{
              flex: 1,
              overflow: "auto",
              paddingBottom: mobileInApp
                ? `calc(${BOTTOM_NAV_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`
                : undefined,
            }}
          >
            <components.TopBar
              acc={acc}
              setPage={navigateToPage}
              locale={locale}
              setLocale={setLocale}
              onLocaleNavigate={handleLocaleNavigate}
              dashboardMeta={dashboardMeta}
              showUpgradeButton={Boolean(authContext.signedIn) && currentPage !== "creditsPlans"}
              analyticsScreen={currentPage}
              isMobile={mobileInApp}
              showCreditsChip={mobileInApp && currentPage !== "creditsPlans"}
              creditsBalance={creditsBalance}
            />
            {pickerKeepAlivePages.map((page) => {
              const slotHidden = page !== revealedPickerPage;
              return (
                <div
                  key={page}
                  className={slotHidden ? "ecliptica-page-slot ecliptica-page-slot--inactive" : "ecliptica-page-slot"}
                  aria-hidden={slotHidden}
                  inert={slotHidden || undefined}
                  data-ecliptica-page={page}
                >
                  <NativePickerSuspendProvider suspended={slotHidden || page === demotedPickerPage}>
                    {renderSignedInPage(page)}
                  </NativePickerSuspendProvider>
                </div>
              );
            })}
            {!IOS_PICKER_KEEP_ALIVE_PAGES.includes(currentPage) && revealedPickerPage === null && renderSignedInPage(currentPage)}
            {currentPage === "creditsPlans" && (
              <>
                <div style={{ padding: `${pageGutterPx(isMobile)}px ${pageGutterPx(isMobile)}px 0` }}>
                  <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 40, fontWeight: 400, color: C.textPrimary }}>{t("credits_and_plans")}</h1>
                  <p style={{ color: C.textSecondary, fontSize: APP_FONT_SIZE, marginTop: 8 }}>{t("upgrade_cosmic_journey")}</p>
                  <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "stretch" }}>
                    <div style={{ maxWidth: 280, padding: "14px 16px", borderRadius: 12, background: `${acc.primary}15`, border: `1px solid ${acc.primary}33` }}>
                      <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.1em", marginBottom: 6 }}>{t("credit_balance_label")}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: `radial-gradient(circle at 35% 35%,${acc.secondary},${acc.primary}88)`, boxShadow: `0 0 12px ${acc.primary}88` }} />
                        <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 500, color: C.textPrimary }}>{creditsBalance}</span>
                        <span style={{ fontSize: 12, color: C.textMuted }}>{t("credits")}</span>
                      </div>
                    </div>
                    <div style={{ minWidth: 260, padding: "14px 16px", borderRadius: 12, background: activeSubscription ? `${acc.secondary}1a` : "rgba(255,255,255,0.04)", border: activeSubscription ? `1px solid ${acc.secondary}66` : "1px solid rgba(255,255,255,0.14)" }}>
                      <div style={{ fontSize: 11, color: activeSubscription ? acc.secondary : C.textMuted, letterSpacing: "0.1em", marginBottom: 6 }}>
                        {activeSubscription ? t("premium_status_label") : t("premium_status_inactive_label")}
                      </div>
                      <div style={{ fontSize: 14, color: C.textPrimary, marginBottom: 4 }}>
                        {activeSubscription ? t("premium_status_active_title") : t("premium_status_inactive_title")}
                      </div>
                      <div style={{ fontSize: 12, color: C.textMuted }}>
                        {activeSubscription ? t("premium_status_active_desc") : t("premium_status_inactive_desc")}
                      </div>
                    </div>
                  </div>
                  <MembershipSection acc={acc} authContext={authContext} t={t} locale={locale} />
                </div>
                <components.CreditsInsightsPage
                  acc={acc}
                  setPage={navigateToPage}
                  locale={locale}
                  formatInTimezone={formatInTimezone}
                  accountStatus={{
                    activeSubscription,
                    creditsBalance,
                  }}
                  billingCatalog={billingCatalog}
                  onSelectPack={(product) => {
                    setSelectedPaymentProduct(product);
                    navigateToPage("payment", {
                      query: {
                        product_code: product?.code,
                        product_type: product?.product_type,
                      },
                    });
                  }}
                  onSelectSubscription={(selectedSubscription) => {
                    const subscriptionProduct =
                      selectedSubscription ||
                      billingCatalog.find((item) => item.code === SUBSCRIPTION_PRODUCT_CODE) ||
                      {
                        code: SUBSCRIPTION_PRODUCT_CODE,
                        product_type: "subscription",
                        credits_amount: 0,
                        // Fallback prices (month interval). PaymentPage will also have URL fallback.
                        prices: [
                          { amount_minor: 199_000, currency: "VND", billing_interval: "month" },
                          { amount_minor: 999, currency: "USD", billing_interval: "month" },
                        ],
                      };

                    setSelectedPaymentProduct(subscriptionProduct);
                    navigateToPage("payment", {
                      query: {
                        product_code: subscriptionProduct.code,
                        product_type: subscriptionProduct.product_type,
                      },
                    });
                  }}
                />
              </>
            )}
          </div>
          {mobileInApp && (
            <components.BottomNav
              page={currentPage}
              setPage={navigateToPage}
              acc={acc}
              analyticsScreen={currentPage}
            />
          )}
        </div>
      ) : currentPage === "pricing" ? (
        <components.PublicPageShell {...publicShellProps} analyticsScreen="pricing">
          <components.PricingSection acc={acc} variant="full" standalone />
          <components.LegalFooterLinks acc={acc} />
        </components.PublicPageShell>
      ) : currentPage === "about" ? (
        <components.PublicPageShell {...publicShellProps} analyticsScreen="about">
          <components.AboutPage setPage={navigateToPage} acc={acc} />
          <components.LegalFooterLinks acc={acc} />
        </components.PublicPageShell>
      ) : currentPage === "terms" ? (
        <components.PublicPageShell {...publicShellProps} analyticsScreen="terms">
          <components.TermsPage acc={acc} />
        </components.PublicPageShell>
      ) : currentPage === "privacy" ? (
        <components.PublicPageShell {...publicShellProps} analyticsScreen="privacy">
          <components.PrivacyPage acc={acc} />
        </components.PublicPageShell>
      ) : currentPage === "contact" ? (
        <components.PublicPageShell {...publicShellProps} analyticsScreen="contact">
          <components.ContactPage acc={acc} />
          <components.LegalFooterLinks acc={acc} />
        </components.PublicPageShell>
      ) : currentPage === "login" ? (
        <>
          <div style={minimalLocaleStyle}>
            <components.LanguageSwitcher locale={locale} onChange={setLocale} acc={acc} onNavigate={handleLocaleNavigate} />
          </div>
          <components.LoginPage setPage={navigateToPage} acc={acc} />
        </>
      ) : currentPage === "emailVerificationRequired" ? (
        <>
          <div style={minimalLocaleStyle}>
            <components.LanguageSwitcher locale={locale} onChange={setLocale} acc={acc} onNavigate={handleLocaleNavigate} />
          </div>
          <components.EmailVerificationRequiredPage acc={acc} authContext={authContext} t={t} />
        </>
      ) : currentPage === "payment" ? (
        <>
          <div style={minimalLocaleStyle}>
            <components.LanguageSwitcher locale={locale} onChange={setLocale} acc={acc} onNavigate={handleLocaleNavigate} />
          </div>
          <components.PaymentPage
            setPage={navigateToPage}
            acc={acc}
            selectedProduct={selectedPaymentProduct}
            billingCatalog={billingCatalog}
            t={t}
          />
        </>
      ) : (
        <components.PublicPageShell {...publicShellProps} showBrandClick={false} analyticsScreen="landing">
          <components.LandingPage setPage={navigateToPage} acc={acc} />
        </components.PublicPageShell>
      )}

    </div>
  );
}
